// ═══════════════════════════════════════════════════════════════
// Generated from specs/sync/sync.yaml (v2) — DO NOT EDIT MANUALLY
// Regenerate: npm run monsterrat:generate:sync
// ═══════════════════════════════════════════════════════════════

import { updatePreferences, syncUser, getUserProfile, getCourses } from '../api/api-client';
import type { AxiosInstance } from 'axios';
import type { UpdatePreferencesRequest, SyncUserRequest } from '../api/http-contracts';
import { UserProfileSchema, CoursesResponseSchema, ActiveMessagesResponseSchema } from '../api/http-contracts';
import { z } from 'zod';
import { createLogger } from '../../utils/logger';
import { freshnessManager } from '../../services/freshnessManager';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { preferencesMigrations } from '../../services/deviceMigrations';

const _log = createLogger('SyncConfig');

/** AsyncStorage keys — generated from sync-config.yaml */
export const STORAGE_KEYS = {
  USER_PROFILE_CACHE: '@fluencia/userProfile/cache',
  COURSES_CACHE: '@fluencia/courses/cache',
  MESSAGES_CACHE: '@fluencia/messages/cache',
  DEVICE_PREFERENCES: '@fluencia/device/preferences',
  DEVICE_INSTALLATION_ID: '@fluencia/device/installationId',
  SYNC_VERSION: '@fluencia/sync/version',
  SYNC_FRESHNESS_PREFIX: '@fluencia/sync/freshness/',
  SYNC_QUEUE: '@fluencia/sync/queue',
} as const;

/** Push endpoint keys — generated from sync-config.yaml push groups */
export type SyncEndpoint =
  | 'PATCH /api/user/preferences'
  | 'POST /api/user/sync';

/** Pushable field names — generated from all push groups */
export type SyncableField =
  | 'displayName'
  | 'debugContextSnapshots'
  | 'lessonFeedbackEnabled'
  | 'voiceExercisesEnabled'
  | 'coursePreferences'
  | 'onboardingCompleted';

/** Maps each pushable field to its endpoint — generated from push groups */
export const FIELD_TO_ENDPOINT: Record<SyncableField, SyncEndpoint> = {
  displayName: 'PATCH /api/user/preferences',
  debugContextSnapshots: 'PATCH /api/user/preferences',
  lessonFeedbackEnabled: 'PATCH /api/user/preferences',
  voiceExercisesEnabled: 'PATCH /api/user/preferences',
  coursePreferences: 'PATCH /api/user/preferences',
  onboardingCompleted: 'POST /api/user/sync',
};

/**
 * Dispatch a batched sync queue entry to the correct API endpoint.
 * Called by the sync queue with all pending fields for one endpoint.
 * Generated from sync-config.yaml + AsyncAPI endpoint registry.
 */
export async function dispatchSync(
  client: AxiosInstance,
  endpoint: SyncEndpoint,
  fields: Record<string, unknown>,
): Promise<void> {
  switch (endpoint) {
    case 'PATCH /api/user/preferences': {
      const _res = await updatePreferences(client, fields as UpdatePreferencesRequest);
      if (!_res.ok) throw _res;
      await UserProfileCacheStorage.save(_res.data as unknown as UserProfileCacheData);
      freshnessManager.markVersionCurrent('userProfile');
      break;
    }
    case 'POST /api/user/sync': {
      const _res = await syncUser(client, fields as SyncUserRequest);
      if (!_res.ok) throw _res;
      await UserProfileCacheStorage.save(_res.data as unknown as UserProfileCacheData);
      freshnessManager.markVersionCurrent('userProfile');
      break;
    }
  }
}

// ═══════════════════════════════════════════════════════════════
// Push Functions — typed entry points for consumers
// ═══════════════════════════════════════════════════════════════

/** Pushable fields for PATCH /api/user/preferences — generated from push group */
export interface UpdatePreferencesPushFields {
  displayName?: string;
  debugContextSnapshots?: boolean;
  lessonFeedbackEnabled?: boolean;
  voiceExercisesEnabled?: boolean;
  coursePreferences?: Array<{
      courseId: string;
      country: string;
      sourceLocale: string;
      targetLocale: string;
      lessonLanguage: 'source' | 'target';
      azureTtsVoice: string;
      ttsProvider: 'device' | 'azure-server';
      sttProvider: 'device' | 'azure';
    }>;
}

/**
 * Enqueue fields for batched push to PATCH /api/user/preferences.
 * Fields are merged into a single API call via the sync queue (debounce + coalesce).
 */
export function pushPreferences(fields: UpdatePreferencesPushFields): void {
  const { syncQueue } = require('../../services/syncQueue');
  for (const [key, value] of Object.entries(fields)) {
    if (value !== undefined) syncQueue.enqueue(key as SyncableField, value);
  }
}

/** Pushable fields for POST /api/user/sync — generated from push group */
export interface SyncUserPushFields {
  onboardingCompleted?: boolean;
}

/**
 * Enqueue fields for batched push to POST /api/user/sync.
 * Fields are merged into a single API call via the sync queue (debounce + coalesce).
 */
export function pushUser(fields: SyncUserPushFields): void {
  const { syncQueue } = require('../../services/syncQueue');
  for (const [key, value] of Object.entries(fields)) {
    if (value !== undefined) syncQueue.enqueue(key as SyncableField, value);
  }
}


// ═══════════════════════════════════════════════════════════════
// Domain Refresh — fetch from endpoint → save raw to cache
// ═══════════════════════════════════════════════════════════════

/**
 * Refresh 'userProfile' domain: fetch from GET /api/user/me → save to userProfileCache.
 * Generated from sync-config.yaml domain definition.
 */
export async function refreshUserProfile(client: AxiosInstance): Promise<void> {
  const response = await getUserProfile(client);
  if (!response.ok) throw response;
  await UserProfileCacheStorage.save(response.data as unknown as UserProfileCacheData);
}

/**
 * Refresh 'courses' domain: fetch from GET /api/courses → save to coursesCache.
 * Generated from sync-config.yaml domain definition.
 */
export async function refreshCourses(client: AxiosInstance): Promise<void> {
  const response = await getCourses(client);
  if (!response.ok) throw response;
  await CoursesCacheStorage.save(response.data as unknown as CoursesCacheData);
}


/** Data freshness domains — generated from sync-config.yaml */
export type DataDomain = 'userProfile' | 'courses' | 'messages' | 'lessons';

export const KNOWN_DOMAINS = new Set<DataDomain>(['userProfile', 'courses', 'messages', 'lessons']);

export interface FreshnessPolicyConfig {
  domain: DataDomain;
  ttlMs: number | null;
  refresh: string;
  versionTracking: boolean;
}

/** Freshness policies — generated from sync-config.yaml */
export const FRESHNESS_POLICIES: FreshnessPolicyConfig[] = [
  {
    domain: 'userProfile',
    ttlMs: 86400000,
    refresh: 'GET /api/user/me',
    versionTracking: true,
  },
  {
    domain: 'courses',
    ttlMs: 86400000,
    refresh: 'GET /api/courses',
    versionTracking: true,
  },
  {
    domain: 'messages',
    ttlMs: 3600000,
    refresh: 'GET /api/messages/active',
    versionTracking: false,
  },
  {
    domain: 'lessons',
    ttlMs: null,
    refresh: 'POST /api/lessons/check-updates',
    versionTracking: true,
  },
];

/** Queue configuration — generated from sync-config.yaml */
export const QUEUE_CONFIG = {
  schemaVersion: 3,
  retry: {
    initialDelay: 1000,
    maxDelay: 30000,
    backoffFactor: 2,
    jitter: 0.3,
    maxRetries: 10,
  },
  debounce: 300,
  heartbeat: 60000,
} as const;

/** Freshness HTTP headers — generated from sync-config.yaml */
export const VERSION_HEADER = 'X-Data-Version';
export const STALE_DOMAINS_HEADER = 'X-Stale-Domains';

// ═══════════════════════════════════════════════════════════════
// Cache Schemas & Versions — Zod validation for cache reads
// ═══════════════════════════════════════════════════════════════

/** Zod schema for userProfileCache — re-exports UserProfile (passthrough) */
export const UserProfileCacheSchema = UserProfileSchema;

/** Zod schema for coursesCache — re-exports CoursesResponse (passthrough) */
export const CoursesCacheSchema = CoursesResponseSchema;

/** Zod schema for messagesCache — extends ActiveMessagesResponse with extra fields */
export const MessagesCacheSchema = ActiveMessagesResponseSchema.extend({
  dismissed: z.array(z.unknown()).optional(),
});

/** Cache version hashes — derived from schema definitions. Changes when schema changes. */
export const CACHE_VERSIONS = {
  userProfileCache: 'b6ec39',
  coursesCache: '4c5d41',
  messagesCache: 'b7275e',
} as const;

// ═══════════════════════════════════════════════════════════════
// Domain Registry — capabilities of each data domain
// ═══════════════════════════════════════════════════════════════

/** Domain registry — generated from sync-config.yaml v2 */
export const DOMAIN_REGISTRY = {
  userProfile: {
    pullEndpoint: 'GET /api/user/me',
    cacheKey: '@fluencia/userProfile/cache',
    ttlMs: 86400000,
    versionTracking: true,
    hasPush: true,
  },
  courses: {
    pullEndpoint: 'GET /api/courses',
    cacheKey: '@fluencia/courses/cache',
    ttlMs: 86400000,
    versionTracking: true,
    hasPush: false,
    fallback: 'static',
  },
  messages: {
    pullEndpoint: 'GET /api/messages/active',
    cacheKey: '@fluencia/messages/cache',
    ttlMs: 3600000,
    versionTracking: false,
    hasPush: false,
  },
  lessons: {
    pullEndpoint: 'POST /api/lessons/check-updates',
    cacheKey: null,
    ttlMs: null,
    versionTracking: true,
    hasPush: false,
  },
} as const;

/**
 * Maps domain names to their generated refresh functions.
 * Use with freshnessManager.register() to wire up domain refreshes.
 * Domains without a cache (e.g. lessons) or with `refresh: custom`
 * (e.g. messages) are excluded — register those manually. `satisfies`
 * keeps the inferred key set exact, so accessing an excluded domain is
 * a compile error, not a runtime undefined.
 */
export const DOMAIN_REFRESH_MAP = {
  userProfile: refreshUserProfile,
  courses: refreshCourses,
} satisfies Partial<Record<DataDomain, (client: AxiosInstance) => Promise<void>>>;

/** Maps push endpoints to the domains they affect — derived from push groups */
export const STALE_DOMAIN_MAP: Record<string, DataDomain> = {
  'PATCH /api/user/preferences': 'userProfile',
  'POST /api/user/sync': 'userProfile',
};

/**
 * Maps each domain's PULL endpoint to the domain it refreshes.
 * Inverse of `FRESHNESS_POLICIES[].refresh` — consumed by the
 * response interceptor to skip self-referential refresh cascades
 * (the `/me` response carrying `X-Stale-Domains: userProfile`
 * shouldn't trigger another `/me` — the response IS the refresh).
 */
export const PULL_ENDPOINT_DOMAIN_MAP: Record<string, DataDomain> = {
  'GET /api/user/me': 'userProfile',
  'GET /api/courses': 'courses',
  'GET /api/messages/active': 'messages',
  'POST /api/lessons/check-updates': 'lessons',
};

// ═══════════════════════════════════════════════════════════════
// Storage Accessors — typed read/write for each storage entry
// ═══════════════════════════════════════════════════════════════

// Dev-only storage observer — streams mutations AND reads to dev-dashboard.
// Mutations land on every save/update/clear; reads land on every load()
// invocation so the E2E runner can capture full AsyncStorage activity in
// the unified call log alongside HTTP / socket / UI / dev-intent.
// eslint-disable-next-line no-var
let _notifyChange: ((key: string, action: string, oldValue: unknown, newValue: unknown) => void) | undefined;
// eslint-disable-next-line no-var
let _notifyRead: ((key: string, value: unknown) => void) | undefined;
if (__DEV__) {
  _notifyChange = require('../../dev/storage-observer').notifyChange;
  _notifyRead = require('../../dev/storage-observer').notifyRead;
}

/** userProfileCache */
export interface UserProfileCacheData {
  id: string;
  email: string;
  name?: string;
  displayName?: string;
  voiceExercisesEnabled?: boolean;
  coursePreferences?: Array<{
      courseId: string;
      country: string;
      sourceLocale: string;
      targetLocale: string;
      lessonLanguage: string;
      azureTtsVoice: string;
      ttsProvider: string;
      sttProvider: string;
    }>;
  maxCourses?: number;
  onboardingCompleted?: boolean;
  debugContextSnapshots?: boolean;
  lessonFeedbackEnabled?: boolean;
  preferencesUpdatedAt?: string;
  aiConsentGrantedAt?: string | null;
  aiConsentVersion?: number;
  createdAt: string;
  updatedAt: string;
  subscription?: {
    hasAccess: boolean;
    entitlementId?: string;
    productId?: string;
    expiresAt?: string;
    isGracePeriod: boolean;
    source: string;
    trialDaysLeft?: number;
    verifiedAt?: string;
  };
  [key: string]: unknown;
}

/** Storage accessor for userProfileCache */
export const UserProfileCacheStorage = {
  async load(): Promise<UserProfileCacheData | null> {
    const raw = await AsyncStorage.getItem(STORAGE_KEYS.USER_PROFILE_CACHE);
    if (!raw) { if (__DEV__ && _notifyRead) _notifyRead(STORAGE_KEYS.USER_PROFILE_CACHE, null); return null; }
    try {
      const obj = JSON.parse(raw);
      const _versionMatch = obj._cacheVersion === CACHE_VERSIONS.userProfileCache;
      const { _cacheVersion, ...data } = obj;
      const parsed = UserProfileCacheSchema.safeParse(data);

      if (parsed.success) {
        // Data is structurally valid — return it even if version changed
        if (!_versionMatch) {
          _log.info('Cache version outdated but data still valid, scheduling refresh', { key: STORAGE_KEYS.USER_PROFILE_CACHE, stored: obj._cacheVersion, expected: CACHE_VERSIONS.userProfileCache });
          freshnessManager.refreshDomains(['userProfile'] as DataDomain[]).catch(() => {});
        }
        if (__DEV__ && _notifyRead) _notifyRead(STORAGE_KEYS.USER_PROFILE_CACHE, parsed.data);
        return parsed.data as UserProfileCacheData;
      }

      // Zod validation failed — data is corrupted or truly incompatible
      const _failurePayload = { key: STORAGE_KEYS.USER_PROFILE_CACHE, versionMatch: _versionMatch, error: parsed.error.message };
      if (_versionMatch) {
        // Schema diverged despite version hash matching — usually means the
        // server response shape doesn't match the declared cache schema.
        // Consumers with Sentry-on-error wiring get this as an issue.
        _log.error('Invalid cache data despite matching version — server schema may have diverged', _failurePayload);
      } else {
        // Cache from a prior schema version. Expected during codegen evolution.
        _log.warn('Invalid cache data from prior schema version, discarding', _failurePayload);
      }
      await AsyncStorage.removeItem(STORAGE_KEYS.USER_PROFILE_CACHE);
      freshnessManager.refreshDomains(['userProfile'] as DataDomain[]).catch(() => {});
      if (__DEV__ && _notifyRead) _notifyRead(STORAGE_KEYS.USER_PROFILE_CACHE, null);
      return null;
    } catch {
      // JSON parse failed — corrupted storage
      await AsyncStorage.removeItem(STORAGE_KEYS.USER_PROFILE_CACHE);
      if (__DEV__ && _notifyRead) _notifyRead(STORAGE_KEYS.USER_PROFILE_CACHE, null);
      return null;
    }
  },
  async save(data: UserProfileCacheData): Promise<void> {
    const _prev = __DEV__ && _notifyChange ? await AsyncStorage.getItem(STORAGE_KEYS.USER_PROFILE_CACHE) : undefined;
    const _validated = UserProfileCacheSchema.safeParse(data);
    if (!_validated.success) {
      _log.error('Invalid data written to cache — fix the writer', { key: STORAGE_KEYS.USER_PROFILE_CACHE, error: _validated.error.message });
    }
    const _wrapped = { ...data, _cacheVersion: CACHE_VERSIONS.userProfileCache };
    await AsyncStorage.setItem(STORAGE_KEYS.USER_PROFILE_CACHE, JSON.stringify(_wrapped));
    if (__DEV__ && _notifyChange) _notifyChange(STORAGE_KEYS.USER_PROFILE_CACHE, 'save', _prev ? JSON.parse(_prev) : null, _wrapped);
  },
  async update(partial: Partial<UserProfileCacheData>): Promise<void> {
    const current = await this.load();
    if (!current) return;
    const _merged = { ...current, ...partial };
    const _validated = UserProfileCacheSchema.safeParse(_merged);
    if (!_validated.success) {
      _log.error('Invalid data written to cache — fix the writer', { key: STORAGE_KEYS.USER_PROFILE_CACHE, error: _validated.error.message });
    }
    const _wrapped = { ..._merged, _cacheVersion: CACHE_VERSIONS.userProfileCache };
    await AsyncStorage.setItem(STORAGE_KEYS.USER_PROFILE_CACHE, JSON.stringify(_wrapped));
    if (__DEV__ && _notifyChange) _notifyChange(STORAGE_KEYS.USER_PROFILE_CACHE, 'update', current, _wrapped);
  },
  async clear(): Promise<void> {
    if (__DEV__ && _notifyChange) { const _prev = await AsyncStorage.getItem(STORAGE_KEYS.USER_PROFILE_CACHE); _notifyChange(STORAGE_KEYS.USER_PROFILE_CACHE, 'clear', _prev ? JSON.parse(_prev) : null, null); }
    await AsyncStorage.removeItem(STORAGE_KEYS.USER_PROFILE_CACHE);
  },
};

/** coursesCache */
export interface CoursesCacheData {
  courses: Array<{
      id: string;
      sourceBase: string;
      targetBase: string;
      defaultSourceLocale: string;
      defaultTargetLocale: string;
      sourceVariants: string[];
      targetVariants: string[];
      hasLessons: boolean;
    }>;
  locales: Array<{
      code: string;
      capabilities: {
        tts: boolean;
        stt: boolean;
        pronunciation: boolean;
        translation: boolean;
      };
      speech: {
        azure?: {
          ttsVoice?: string;
          sttLocale?: string;
          availableVoices?: Array<{
              id: string;
              label: string;
            }>;
          pronunciationFeatures?: {
            phonemeLabels: boolean;
            syllables: boolean;
            nbestPhonemes: boolean;
            prosody: boolean;
          };
        };
      };
      native: {
        language: string;
        variant: string;
        variantHint: string;
        testPhrase: string;
      };
    }>;
  countries: Array<{
      code: string;
      emoji: string;
      locales: string[];
    }>;
  [key: string]: unknown;
}

/** Storage accessor for coursesCache */
export const CoursesCacheStorage = {
  async load(): Promise<CoursesCacheData | null> {
    const raw = await AsyncStorage.getItem(STORAGE_KEYS.COURSES_CACHE);
    if (!raw) { if (__DEV__ && _notifyRead) _notifyRead(STORAGE_KEYS.COURSES_CACHE, null); return null; }
    try {
      const obj = JSON.parse(raw);
      const _versionMatch = obj._cacheVersion === CACHE_VERSIONS.coursesCache;
      const { _cacheVersion, ...data } = obj;
      const parsed = CoursesCacheSchema.safeParse(data);

      if (parsed.success) {
        // Data is structurally valid — return it even if version changed
        if (!_versionMatch) {
          _log.info('Cache version outdated but data still valid, scheduling refresh', { key: STORAGE_KEYS.COURSES_CACHE, stored: obj._cacheVersion, expected: CACHE_VERSIONS.coursesCache });
          freshnessManager.refreshDomains(['courses'] as DataDomain[]).catch(() => {});
        }
        if (__DEV__ && _notifyRead) _notifyRead(STORAGE_KEYS.COURSES_CACHE, parsed.data);
        return parsed.data as CoursesCacheData;
      }

      // Zod validation failed — data is corrupted or truly incompatible
      const _failurePayload = { key: STORAGE_KEYS.COURSES_CACHE, versionMatch: _versionMatch, error: parsed.error.message };
      if (_versionMatch) {
        // Schema diverged despite version hash matching — usually means the
        // server response shape doesn't match the declared cache schema.
        // Consumers with Sentry-on-error wiring get this as an issue.
        _log.error('Invalid cache data despite matching version — server schema may have diverged', _failurePayload);
      } else {
        // Cache from a prior schema version. Expected during codegen evolution.
        _log.warn('Invalid cache data from prior schema version, discarding', _failurePayload);
      }
      await AsyncStorage.removeItem(STORAGE_KEYS.COURSES_CACHE);
      freshnessManager.refreshDomains(['courses'] as DataDomain[]).catch(() => {});
      if (__DEV__ && _notifyRead) _notifyRead(STORAGE_KEYS.COURSES_CACHE, null);
      return null;
    } catch {
      // JSON parse failed — corrupted storage
      await AsyncStorage.removeItem(STORAGE_KEYS.COURSES_CACHE);
      if (__DEV__ && _notifyRead) _notifyRead(STORAGE_KEYS.COURSES_CACHE, null);
      return null;
    }
  },
  async save(data: CoursesCacheData): Promise<void> {
    const _prev = __DEV__ && _notifyChange ? await AsyncStorage.getItem(STORAGE_KEYS.COURSES_CACHE) : undefined;
    const _validated = CoursesCacheSchema.safeParse(data);
    if (!_validated.success) {
      _log.error('Invalid data written to cache — fix the writer', { key: STORAGE_KEYS.COURSES_CACHE, error: _validated.error.message });
    }
    const _wrapped = { ...data, _cacheVersion: CACHE_VERSIONS.coursesCache };
    await AsyncStorage.setItem(STORAGE_KEYS.COURSES_CACHE, JSON.stringify(_wrapped));
    if (__DEV__ && _notifyChange) _notifyChange(STORAGE_KEYS.COURSES_CACHE, 'save', _prev ? JSON.parse(_prev) : null, _wrapped);
  },
  async update(partial: Partial<CoursesCacheData>): Promise<void> {
    const current = await this.load();
    if (!current) return;
    const _merged = { ...current, ...partial };
    const _validated = CoursesCacheSchema.safeParse(_merged);
    if (!_validated.success) {
      _log.error('Invalid data written to cache — fix the writer', { key: STORAGE_KEYS.COURSES_CACHE, error: _validated.error.message });
    }
    const _wrapped = { ..._merged, _cacheVersion: CACHE_VERSIONS.coursesCache };
    await AsyncStorage.setItem(STORAGE_KEYS.COURSES_CACHE, JSON.stringify(_wrapped));
    if (__DEV__ && _notifyChange) _notifyChange(STORAGE_KEYS.COURSES_CACHE, 'update', current, _wrapped);
  },
  async clear(): Promise<void> {
    if (__DEV__ && _notifyChange) { const _prev = await AsyncStorage.getItem(STORAGE_KEYS.COURSES_CACHE); _notifyChange(STORAGE_KEYS.COURSES_CACHE, 'clear', _prev ? JSON.parse(_prev) : null, null); }
    await AsyncStorage.removeItem(STORAGE_KEYS.COURSES_CACHE);
  },
};

/** messagesCache */
export interface MessagesCacheData {
  dismissed?: Array<string>;
  messages: Array<{
      id: string;
      displayType: 'banner' | 'modal' | 'blocking';
      i18nKey: string;
      priority: number;
      dismissable: boolean;
      actionType?: string;
      actionPayload?: string;
    }>;
  [key: string]: unknown;
}

/** Storage accessor for messagesCache */
export const MessagesCacheStorage = {
  async load(): Promise<MessagesCacheData | null> {
    const raw = await AsyncStorage.getItem(STORAGE_KEYS.MESSAGES_CACHE);
    if (!raw) { if (__DEV__ && _notifyRead) _notifyRead(STORAGE_KEYS.MESSAGES_CACHE, null); return null; }
    try {
      const obj = JSON.parse(raw);
      const _versionMatch = obj._cacheVersion === CACHE_VERSIONS.messagesCache;
      const { _cacheVersion, ...data } = obj;
      const parsed = MessagesCacheSchema.safeParse(data);

      if (parsed.success) {
        // Data is structurally valid — return it even if version changed
        if (!_versionMatch) {
          _log.info('Cache version outdated but data still valid, scheduling refresh', { key: STORAGE_KEYS.MESSAGES_CACHE, stored: obj._cacheVersion, expected: CACHE_VERSIONS.messagesCache });
          freshnessManager.refreshDomains(['messages'] as DataDomain[]).catch(() => {});
        }
        if (__DEV__ && _notifyRead) _notifyRead(STORAGE_KEYS.MESSAGES_CACHE, parsed.data);
        return parsed.data as MessagesCacheData;
      }

      // Zod validation failed — data is corrupted or truly incompatible
      const _failurePayload = { key: STORAGE_KEYS.MESSAGES_CACHE, versionMatch: _versionMatch, error: parsed.error.message };
      if (_versionMatch) {
        // Schema diverged despite version hash matching — usually means the
        // server response shape doesn't match the declared cache schema.
        // Consumers with Sentry-on-error wiring get this as an issue.
        _log.error('Invalid cache data despite matching version — server schema may have diverged', _failurePayload);
      } else {
        // Cache from a prior schema version. Expected during codegen evolution.
        _log.warn('Invalid cache data from prior schema version, discarding', _failurePayload);
      }
      await AsyncStorage.removeItem(STORAGE_KEYS.MESSAGES_CACHE);
      freshnessManager.refreshDomains(['messages'] as DataDomain[]).catch(() => {});
      if (__DEV__ && _notifyRead) _notifyRead(STORAGE_KEYS.MESSAGES_CACHE, null);
      return null;
    } catch {
      // JSON parse failed — corrupted storage
      await AsyncStorage.removeItem(STORAGE_KEYS.MESSAGES_CACHE);
      if (__DEV__ && _notifyRead) _notifyRead(STORAGE_KEYS.MESSAGES_CACHE, null);
      return null;
    }
  },
  async save(data: MessagesCacheData): Promise<void> {
    const _prev = __DEV__ && _notifyChange ? await AsyncStorage.getItem(STORAGE_KEYS.MESSAGES_CACHE) : undefined;
    const _validated = MessagesCacheSchema.safeParse(data);
    if (!_validated.success) {
      _log.error('Invalid data written to cache — fix the writer', { key: STORAGE_KEYS.MESSAGES_CACHE, error: _validated.error.message });
    }
    const _wrapped = { ...data, _cacheVersion: CACHE_VERSIONS.messagesCache };
    await AsyncStorage.setItem(STORAGE_KEYS.MESSAGES_CACHE, JSON.stringify(_wrapped));
    if (__DEV__ && _notifyChange) _notifyChange(STORAGE_KEYS.MESSAGES_CACHE, 'save', _prev ? JSON.parse(_prev) : null, _wrapped);
  },
  async update(partial: Partial<MessagesCacheData>): Promise<void> {
    const current = await this.load();
    if (!current) return;
    const _merged = { ...current, ...partial };
    const _validated = MessagesCacheSchema.safeParse(_merged);
    if (!_validated.success) {
      _log.error('Invalid data written to cache — fix the writer', { key: STORAGE_KEYS.MESSAGES_CACHE, error: _validated.error.message });
    }
    const _wrapped = { ..._merged, _cacheVersion: CACHE_VERSIONS.messagesCache };
    await AsyncStorage.setItem(STORAGE_KEYS.MESSAGES_CACHE, JSON.stringify(_wrapped));
    if (__DEV__ && _notifyChange) _notifyChange(STORAGE_KEYS.MESSAGES_CACHE, 'update', current, _wrapped);
  },
  async clear(): Promise<void> {
    if (__DEV__ && _notifyChange) { const _prev = await AsyncStorage.getItem(STORAGE_KEYS.MESSAGES_CACHE); _notifyChange(STORAGE_KEYS.MESSAGES_CACHE, 'clear', _prev ? JSON.parse(_prev) : null, null); }
    await AsyncStorage.removeItem(STORAGE_KEYS.MESSAGES_CACHE);
  },
};

/** Device-scoped UI settings (theme, audio, TTS). Survives logout. */
export interface PreferencesData {
  activeCourseId: string | null;
  colorMode: 'light' | 'dark' | 'system';
  audioMode: 'mute' | 'auto_play';
  ttsAutoPlay: boolean;
  ttsProvider: 'device' | 'azure-server';
  ttsVoice: string;
  uiLanguage: string;
  [key: string]: unknown;
}

/**
 * Migration steps for preferences — key K maps stored data at version K
 * to version K+1 (a missing `_v` = version 0, the pre-versioning shape).
 * Bumping `schemaVersion` in specs/sync/sync.yaml adds a required key here;
 * the build fails until the registry ('../../services/deviceMigrations')
 * implements the new step. Steps must be total: never throw on missing
 * fields — device data has no server copy to re-fetch.
 */
export interface PreferencesMigrations {
  0: (old: Record<string, unknown>) => Record<string, unknown>;
  1: (old: Record<string, unknown>) => Record<string, unknown>;
  2: (old: Record<string, unknown>) => PreferencesData;
}

/** Storage accessor for preferences */
export const PreferencesStorage = {
  async load(): Promise<PreferencesData | null> {
    const raw = await AsyncStorage.getItem(STORAGE_KEYS.DEVICE_PREFERENCES);
    if (!raw) { if (__DEV__ && _notifyRead) _notifyRead(STORAGE_KEYS.DEVICE_PREFERENCES, null); return null; }
    try {
      let _parsed = JSON.parse(raw) as Record<string, unknown>;
      const _rawV = _parsed._v;
      const _storedV = typeof _rawV === 'number' && Number.isInteger(_rawV) && _rawV >= 0 ? _rawV : 0;
      if (_storedV < 3) {
        try {
          const _steps = preferencesMigrations as unknown as Record<number, (old: Record<string, unknown>) => Record<string, unknown>>;
          for (let _v = _storedV; _v < 3; _v++) _parsed = _steps[_v]!(_parsed);
          _parsed = { ..._parsed, _v: 3 };
          // Persist the migrated payload so the chain runs once, not on every read.
          await AsyncStorage.setItem(STORAGE_KEYS.DEVICE_PREFERENCES, JSON.stringify(_parsed));
        } catch {
          // Migration failed — keep the un-migrated payload; the consumer's
          // defaults-merge covers missing fields. Never discard device-owned
          // data (it has no server source to re-fetch from).
        }
      }
      // _storedV > 3 (OTA rollback / downgrade): keep the payload as-is —
      // unknown fields ride the index signature and survive round-trips.
      if (__DEV__ && _notifyRead) _notifyRead(STORAGE_KEYS.DEVICE_PREFERENCES, _parsed);
      return _parsed as PreferencesData;
    } catch { if (__DEV__ && _notifyRead) _notifyRead(STORAGE_KEYS.DEVICE_PREFERENCES, null); return null; }
  },
  async save(data: PreferencesData): Promise<void> {
    const _prev = __DEV__ && _notifyChange ? await AsyncStorage.getItem(STORAGE_KEYS.DEVICE_PREFERENCES) : undefined;
    const _wrapped = { ...data, _v: 3 };
    await AsyncStorage.setItem(STORAGE_KEYS.DEVICE_PREFERENCES, JSON.stringify(_wrapped));
    if (__DEV__ && _notifyChange) _notifyChange(STORAGE_KEYS.DEVICE_PREFERENCES, 'save', _prev ? JSON.parse(_prev) : null, _wrapped);
  },
  async update(partial: Partial<PreferencesData>): Promise<void> {
    const current = await this.load();
    if (!current) return;
    const _merged = { ...current, ...partial };
    const _wrapped = { ..._merged, _v: 3 };
    await AsyncStorage.setItem(STORAGE_KEYS.DEVICE_PREFERENCES, JSON.stringify(_wrapped));
    if (__DEV__ && _notifyChange) _notifyChange(STORAGE_KEYS.DEVICE_PREFERENCES, 'update', current, _wrapped);
  },
  async clear(): Promise<void> {
    if (__DEV__ && _notifyChange) { const _prev = await AsyncStorage.getItem(STORAGE_KEYS.DEVICE_PREFERENCES); _notifyChange(STORAGE_KEYS.DEVICE_PREFERENCES, 'clear', _prev ? JSON.parse(_prev) : null, null); }
    await AsyncStorage.removeItem(STORAGE_KEYS.DEVICE_PREFERENCES);
  },
};

/** Storage accessor for installationId — scalar value */
export const InstallationIdStorage = {
  async load(): Promise<string | null> {
    const raw = await AsyncStorage.getItem(STORAGE_KEYS.DEVICE_INSTALLATION_ID);
    const _value = raw ?? null;
    if (__DEV__ && _notifyRead) _notifyRead(STORAGE_KEYS.DEVICE_INSTALLATION_ID, _value);
    return _value;
  },
  async save(value: string): Promise<void> {
    const _prev = __DEV__ && _notifyChange ? await AsyncStorage.getItem(STORAGE_KEYS.DEVICE_INSTALLATION_ID) : undefined;
    await AsyncStorage.setItem(STORAGE_KEYS.DEVICE_INSTALLATION_ID, value);
    if (__DEV__ && _notifyChange) _notifyChange(STORAGE_KEYS.DEVICE_INSTALLATION_ID, 'save', _prev ?? null, value);
  },
  async clear(): Promise<void> {
    if (__DEV__ && _notifyChange) { const _prev = await AsyncStorage.getItem(STORAGE_KEYS.DEVICE_INSTALLATION_ID); _notifyChange(STORAGE_KEYS.DEVICE_INSTALLATION_ID, 'clear', _prev ?? null, null); }
    await AsyncStorage.removeItem(STORAGE_KEYS.DEVICE_INSTALLATION_ID);
  },
};

/** Storage accessor for freshnessVersion — scalar value */
export const FreshnessVersionStorage = {
  async load(): Promise<number> {
    const raw = await AsyncStorage.getItem(STORAGE_KEYS.SYNC_VERSION);
    const _value = raw ? Number(raw) || 0 : 0;
    if (__DEV__ && _notifyRead) _notifyRead(STORAGE_KEYS.SYNC_VERSION, _value);
    return _value;
  },
  async save(value: number): Promise<void> {
    const _prev = __DEV__ && _notifyChange ? await AsyncStorage.getItem(STORAGE_KEYS.SYNC_VERSION) : undefined;
    await AsyncStorage.setItem(STORAGE_KEYS.SYNC_VERSION, String(value));
    if (__DEV__ && _notifyChange) _notifyChange(STORAGE_KEYS.SYNC_VERSION, 'save', _prev != null ? Number(_prev) : null, value);
  },
  async clear(): Promise<void> {
    if (__DEV__ && _notifyChange) { const _prev = await AsyncStorage.getItem(STORAGE_KEYS.SYNC_VERSION); _notifyChange(STORAGE_KEYS.SYNC_VERSION, 'clear', _prev ?? null, null); }
    await AsyncStorage.removeItem(STORAGE_KEYS.SYNC_VERSION);
  },
};

/** Per-domain version tracking. Key is prefix + domain name. */
export interface FreshnessDomainStateData {
  lastSyncedAt: number;
  lastCompletedVersion: number;
  lastRequestedVersion: number;
}

/** Storage accessor for freshnessDomainState — prefixed key (key + suffix) */
export const FreshnessDomainStateStorage = {
  async load(suffix: string): Promise<FreshnessDomainStateData | null> {
    const _key = STORAGE_KEYS.SYNC_FRESHNESS_PREFIX + suffix;
    const raw = await AsyncStorage.getItem(_key);
    if (!raw) { if (__DEV__ && _notifyRead) _notifyRead(_key, null); return null; }
    try { const _parsed = JSON.parse(raw); if (__DEV__ && _notifyRead) _notifyRead(_key, _parsed); return _parsed; } catch { if (__DEV__ && _notifyRead) _notifyRead(_key, null); return null; }
  },
  async save(suffix: string, data: FreshnessDomainStateData): Promise<void> {
    const _key = STORAGE_KEYS.SYNC_FRESHNESS_PREFIX + suffix;
    const _prev = __DEV__ && _notifyChange ? await AsyncStorage.getItem(_key) : undefined;
    await AsyncStorage.setItem(_key, JSON.stringify(data));
    if (__DEV__ && _notifyChange) _notifyChange(_key, 'save', _prev ? JSON.parse(_prev) : null, data);
  },
  async clear(suffix: string): Promise<void> {
    const _key = STORAGE_KEYS.SYNC_FRESHNESS_PREFIX + suffix;
    if (__DEV__ && _notifyChange) { const _prev = await AsyncStorage.getItem(_key); _notifyChange(_key, 'clear', _prev ? JSON.parse(_prev) : null, null); }
    await AsyncStorage.removeItem(_key);
  },
};

/** Persisted sync queue entries. Survives app restarts. */
export interface SyncQueuePersistenceData {
  version: number;
  entries: Array<{
      endpoint: SyncEndpoint;
      fields: unknown;
      enqueuedAt: number;
      retries: number;
    }>;
  [key: string]: unknown;
}

/** Storage accessor for syncQueuePersistence */
export const SyncQueuePersistenceStorage = {
  async load(): Promise<SyncQueuePersistenceData | null> {
    const raw = await AsyncStorage.getItem(STORAGE_KEYS.SYNC_QUEUE);
    if (!raw) { if (__DEV__ && _notifyRead) _notifyRead(STORAGE_KEYS.SYNC_QUEUE, null); return null; }
    try { const _parsed = JSON.parse(raw); if (__DEV__ && _notifyRead) _notifyRead(STORAGE_KEYS.SYNC_QUEUE, _parsed); return _parsed; } catch { if (__DEV__ && _notifyRead) _notifyRead(STORAGE_KEYS.SYNC_QUEUE, null); return null; }
  },
  async save(data: SyncQueuePersistenceData): Promise<void> {
    const _prev = __DEV__ && _notifyChange ? await AsyncStorage.getItem(STORAGE_KEYS.SYNC_QUEUE) : undefined;
    await AsyncStorage.setItem(STORAGE_KEYS.SYNC_QUEUE, JSON.stringify(data));
    if (__DEV__ && _notifyChange) _notifyChange(STORAGE_KEYS.SYNC_QUEUE, 'save', _prev ? JSON.parse(_prev) : null, data);
  },
  async update(partial: Partial<SyncQueuePersistenceData>): Promise<void> {
    const current = await this.load();
    if (!current) return;
    const _merged = { ...current, ...partial };
    await AsyncStorage.setItem(STORAGE_KEYS.SYNC_QUEUE, JSON.stringify(_merged));
    if (__DEV__ && _notifyChange) _notifyChange(STORAGE_KEYS.SYNC_QUEUE, 'update', current, _merged);
  },
  async clear(): Promise<void> {
    if (__DEV__ && _notifyChange) { const _prev = await AsyncStorage.getItem(STORAGE_KEYS.SYNC_QUEUE); _notifyChange(STORAGE_KEYS.SYNC_QUEUE, 'clear', _prev ? JSON.parse(_prev) : null, null); }
    await AsyncStorage.removeItem(STORAGE_KEYS.SYNC_QUEUE);
  },
};
